import { type Cart, ShippingMethod } from "./types"
import {
  generateUniqueId,
  generateUserFacingId,
  areAllItemsGiftCards,
  hasPreOrderItems,
  isGiftCardItem,
  withFinalDiscountPrice,
} from "./helpers"
import { trackAction } from "@local/tracking/src/trackActions"
import { dispatchDataLayerPush } from "@local/tracking/src/dispatch"
import { objectToShiftedBase64 } from "@local/utils/src/safeBase64"
import { sendBeacon } from "@local/utils/src/sendBeacon"
import { clearDecimals } from "@local/utils/src/clearDecimals"
import { convertToUsd } from "@local/tracking/src/convertToUsd"
import { eraseCleanupCooldown } from "../utils/cleanupCooldown"
import {
  CART_VERSION,
  type ShippingCostConfigType,
  SHIPPING_COSTS_CONFIG,
  SHIPPING_GLOBAL_FALLBACK_CONFIG,
} from "./const"
import { fetchApi } from "@local/utils/src/fetchApi"
import { APP_URL } from "@local/utils/src/services"
import type { CouponHandlerResponse } from "../api/coupon"
import { getLatestDate } from "@local/utils/src/getLatestDate"

/**
 * Handles cart data persistence to localStorage
 * Manages storage operations for cart data using version-based keys with region separation
 */
export class CartStorage {
  private region: string
  private channel: string

  /**
   * Creates a new CartStorage instance
   * @param region - The region identifier for the cart (e.g., 'us', 'eu', 'gb', 'ca')
   * @param channel - The channel identifier for the cart (e.g., 'baerskintactical', 'baerskin-hoodie-4')
   */
  constructor(region: string, channel: string) {
    this.region = region.toLowerCase()
    this.channel = channel.toLowerCase()
  }

  /**
   * Gets the localStorage key for the cart using version-based storage with region
   * @returns The storage key string in format 'cart_v2_r{version}_{channel}_{region}'
   */
  public getStorageKey(): string {
    return `cart_v2_r${CART_VERSION}_${this.channel}_${this.region}`
  }

  /**
   * Saves cart data to localStorage
   * Strips the `listing` property from items to prevent localStorage bloat
   * @param data - The cart state data to save
   */
  public save(data: Cart.StateData): void {
    try {
      // Strip listing from items to reduce localStorage size
      const trimmedData = {
        ...data,
        items: data.items.map((item) => {
          // Also strip listing from bundleItems if present
          if (item.type === "bundle" && item.bundleItems) {
            return {
              ...item,
              listing: undefined,
              bundleItems: item.bundleItems.map((bundleItem) => ({
                ...bundleItem,
                listing: undefined,
              })),
            }
          }
          return {
            ...item,
            listing: undefined,
          }
        }),
      }
      localStorage.setItem(this.getStorageKey(), JSON.stringify(trimmedData))
    } catch (error) {
      console.error("Failed to save cart to localStorage:", error)
    }
  }

  /**
   * Loads cart data from localStorage
   * @returns The saved cart state data, or null if not found or invalid
   */
  public load(): Cart.StateData | null {
    try {
      // Avoid SSR issues
      if (typeof window === "undefined") {
        return null
      }
      const stored = localStorage.getItem(this.getStorageKey())
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.error("Failed to load cart from localStorage:", error)
      return null
    }
  }

  /**
   * Clears cart data from localStorage
   */
  public clear(): void {
    try {
      localStorage.removeItem(this.getStorageKey())
    } catch (error) {
      console.error("Failed to clear cart from localStorage:", error)
    }
  }
}

/**
 * Interface for cart event input
 */
interface CartEventInput {
  action: string
  contents: object
  channel?: string
  cookies?: string | null
  currency?: string | null
  id: string
  operation: "create" | "update" | "delete" | "track"
  snapshot: object
  uid: string
}

/**
 * Interface for tracking options
 */
interface TrackingOptions {
  noTrack?: boolean
  trackActionBuffered?: typeof trackAction
  location?: string
}

/**
 * Manages cart state and operations
 * Handles cart items, totals, discounts, and storage
 */
export class CartManager {
  // Private properties
  private channel: string
  private currency: string
  private customShippingCost?: number
  private convertedCart: string | null = null
  private discount?: Cart.Discount
  private giftCard?: Cart.GiftCard
  private id: string
  private items: Cart.Item[] = []
  private lastUpdated: string
  private listeners: Set<(cart: CartManager) => void> = new Set()
  private progressiveDiscountPercentage: number = 0
  private region: string
  private shippingMethod: ShippingMethod = ShippingMethod.STANDARD
  private storage: CartStorage
  private tax: Cart.TaxInfo
  private tip: number = 0
  private totals: Cart.Totals
  private uid: string

  /**
   * Creates a new CartManager instance
   * @param region - The region identifier for the cart (e.g., 'us', 'eu', 'gb', 'ca')
   */
  constructor(region: string, channel: string) {
    this.region = region.toLowerCase()
    this.channel = channel
    this.currency = this.determineCurrencyForRegion(region.toLowerCase())
    this.id = generateUniqueId()
    this.uid = generateUserFacingId()
    this.storage = new CartStorage(region.toLowerCase(), channel)
    this.totals = this.getEmptyTotals()
    this.tax = { amount: 0, status: "estimated" }
    this.lastUpdated = new Date().toISOString()

    // Initialize from storage if available
    this.loadFromStorage()

    // Auto-clear cart if it was converted to an order
    if (this.convertedCart) {
      this.clearCart({ noTrack: true })
    }

    // Set up cross-tab synchronization
    this.setupSynchronization()
  }

  // Public methods

  /**
   * Adds a new item to the cart
   * @param item - The item to add (without id, index, and createdAt which are auto-generated)
   * @param options - Optional tracking configuration
   */
  public addItem(
    item: Omit<Cart.Item, "id" | "index" | "createdAt">,
    options: TrackingOptions = {}
  ): void {
    const {
      noTrack = false,
      trackActionBuffered = trackAction,
      location,
    } = options

    // Stock validation - prevent adding more items than available stock
    const availableStock = item.stock ?? 999
    const isPreOrder = item.preOrder !== null && item.preOrder !== undefined

    if (!isPreOrder && availableStock <= 0) {
      console.warn("Cannot add item: out of stock")
      return
    }

    // Count existing items with same SKU in cart
    const existingCount = this.items.filter((i) => i.sku === item.sku).length
    if (!isPreOrder && existingCount >= availableStock) {
      console.warn("Cannot add item: would exceed available stock")
      return
    }

    // Strip listing to prevent memory bloat
    const baseItem = {
      ...item,
      discountPrice: item.discountPrice > 0 ? item.discountPrice : item.price,
      listing: undefined,
      id: generateUniqueId(),
      index: this.items.length,
      createdAt: new Date().toISOString(),
    }

    let newItem: Cart.Item

    if (item.type === "bundle") {
      const bundleItem = baseItem as Cart.BundleItem
      // Handle bundle items
      if (bundleItem.bundleItems) {
        bundleItem.bundleItems = bundleItem.bundleItems.map(
          (bundleSubItem, index) => ({
            ...bundleSubItem,
            discountPrice:
              bundleSubItem.discountPrice > 0
                ? bundleSubItem.discountPrice
                : bundleSubItem.price,
            listing: undefined,
            id: bundleSubItem.id || generateUniqueId(),
            parentItemId: bundleItem.id,
            index,
            createdAt: new Date().toISOString(),
            type: "single", // Ensure bundle items are always single type
          })
        )
        bundleItem.preOrder = getLatestDate(
          bundleItem.bundleItems.map((bi) => bi.preOrder)
        )
      }
      newItem = bundleItem
    } else {
      newItem = baseItem as Cart.SingleItem
    }
    this.items.push(newItem)

    // Only need to check the new item — existing items are already validated
    // by setShippingMethod, replaceItem, and loadCartData guards
    if (
      newItem.preOrder !== null &&
      newItem.preOrder !== undefined &&
      this.shippingMethod === ShippingMethod.SUPER_EXPRESS
    ) {
      this.shippingMethod = ShippingMethod.STANDARD
      this.customShippingCost = undefined
    }

    this.recalculateTotals()
    this.saveToStorage()
    // Tracking
    if (!noTrack) {
      this.trackAddToCart([newItem], trackActionBuffered, location)
    }

    this.beamCartEvent({
      action: "addItems",
      contents: { items: [newItem] },
      operation: "update",
    })

    this.emitUpdate()
  }

  /**
   * Removes an item from the cart by ID, including dependent items
   * @param itemId - The unique identifier of the item to remove
   * @param options - Optional tracking configuration
   */
  public removeItem(itemId: string, options: TrackingOptions = {}): void {
    const { noTrack = false } = options
    const itemToRemove = this.items.find((item) => item.id === itemId)

    if (!itemToRemove) return

    const itemsToRemove: string[] = [itemId]

    // If removing a bundle, remove all its items
    if (itemToRemove.type === "bundle" && itemToRemove.bundleItems) {
      itemsToRemove.push(...itemToRemove.bundleItems.map((item) => item.id))
    }

    // If removing a promotional item, check for dependent items
    const childItems = this.getItems()?.filter(
      (item) => item.parentItemId === itemToRemove.id
    )
    if (childItems) {
      itemsToRemove.push(...childItems.map((item) => item.id))
    }

    // Remove all identified items
    this.items = this.items.filter((item) => !itemsToRemove.includes(item.id))

    // Reindex remaining items
    this.reindexItems()
    this.recalculateTotals()
    this.saveToStorage()

    // Tracking
    if (!noTrack) {
      this.trackRemoveFromCart(itemToRemove)
    }

    this.beamCartEvent({
      action: "removeItem",
      contents: { itemId },
      operation: "update",
    })

    this.emitUpdate()
  }

  /**
   * Replaces an existing cart item with a new one
   * @param itemId - The ID of the item to replace
   * @param newItem - The new item data (ID will be preserved)
   * @returns Whether the item was found and replaced
   */
  public replaceItem(
    itemId: string,
    newItem: Omit<Cart.Item, "id" | "index" | "createdAt">
  ): boolean {
    const existingIndex = this.items.findIndex((item) => item.id === itemId)
    if (existingIndex === -1) {
      const error = new Error(`Item with id ${itemId} not found`)
      console.error(error.message)
      return false
    }

    // Strip listing to prevent memory bloat
    const baseItem = {
      ...newItem,
      discountPrice:
        newItem.discountPrice > 0 ? newItem.discountPrice : newItem.price,
      listing: undefined,
      id: itemId, // Keep the same ID
      index: existingIndex,
      createdAt: new Date().toISOString(),
    }
    let replacementItem: Cart.Item

    if (newItem.type === "bundle") {
      const bundleItem = baseItem as Cart.BundleItem
      if (bundleItem.bundleItems) {
        bundleItem.bundleItems = bundleItem.bundleItems.map(
          (bundleSubItem) => ({
            ...bundleSubItem,
            discountPrice:
              bundleSubItem.discountPrice > 0
                ? bundleSubItem.discountPrice
                : bundleSubItem.price,
            listing: undefined,
          })
        )
        bundleItem.preOrder = getLatestDate(
          bundleItem.bundleItems.map((bi) => bi.preOrder)
        )
      }
      replacementItem = bundleItem
    } else {
      replacementItem = baseItem as Cart.SingleItem
    }

    this.items[existingIndex] = replacementItem

    // If the replacement is a pre-order item, fall back to standard shipping
    if (
      replacementItem.preOrder !== null &&
      replacementItem.preOrder !== undefined &&
      this.shippingMethod === ShippingMethod.SUPER_EXPRESS
    ) {
      this.shippingMethod = ShippingMethod.STANDARD
      this.customShippingCost = undefined
    }

    this.trackAddToCart([replacementItem])
    this.recalculateTotals()
    this.saveToStorage()
    this.emitUpdate()

    return true
  }

  /**
   * Checks if the cart contains any gift card items
   * @returns True if the cart has at least one gift card item
   */
  public hasGiftCardItems(): boolean {
    return this.items.some(isGiftCardItem)
  }

  /**
   * Applies a coupon code to the cart
   * @param code - The coupon code to validate and apply
   * @throws Will throw an error if the coupon is invalid, expired, or doesn't meet minimum spend requirements
   */
  public async applyCoupon(
    code: string,
    market: string,
    channel: string
  ): Promise<void> {
    // Coupons cannot be applied to gift card purchases
    if (this.hasGiftCardItems()) {
      throw new Error("Coupons cannot be applied to gift card purchases")
    }

    // Coupons and gift cards are mutually exclusive
    if (this.giftCard) {
      this.removeGiftCard({ noTrack: true })
    }

    this.beamCartEvent({
      action: "addCoupon",
      contents: { code },
      operation: "update",
    })

    // Validate coupon with backend
    const couponData = await fetchApi<CouponHandlerResponse>({
      endpoint: "/api/cart/coupon",
      method: "POST",
      options: { skipError: true },
      data: {
        code,
        market,
        channel,
        basket: this.totals.subtotal,
      },
      retries: 1,
    })

    if (!couponData || couponData?.error) {
      this.beamCartEvent({
        action: "addCouponFail",
        contents: { code },
        operation: "update",
      })
      throw new Error(couponData?.error || "Invalid coupon code")
    }

    this.discount = {
      code,
      amount: couponData.amount,
      type: couponData.type as "percentage" | "fixed",
      minSpend: couponData.min ?? 0,
      source: "coupon",
    }

    this.recalculateTotals()
    this.saveToStorage()

    // Tracking
    trackAction("add_coupon", {
      cart_id: this.uid,
      coupon_id: code,
    })

    this.beamCartEvent({
      action: "addedCoupon",
      contents: { code },
      operation: "update",
    })

    this.emitUpdate()
  }

  /**
   * Removes the currently applied coupon from the cart
   * @param options - Optional tracking configuration
   */
  public removeCoupon(options: TrackingOptions = {}): void {
    const { noTrack = false } = options
    const currentCoupon = this.discount

    this.discount = undefined
    this.applyProgressiveIfEligible()
    this.recalculateTotals()
    this.saveToStorage()

    // Tracking
    if (!noTrack) {
      this.beamCartEvent({
        action: "removeCoupon",
        contents: { coupon: currentCoupon },
        operation: "update",
      })

      trackAction("remove_coupon", {
        cart_id: this.uid,
        code: currentCoupon?.code,
      })
    }

    this.emitUpdate()
  }

  /**
   * Applies a gift card to the cart
   * @param code - The gift card code to validate and apply
   * @throws Will throw an error if the gift card is invalid or has insufficient balance
   */
  public async applyGiftCard(code: string): Promise<void> {
    // Gift cards cannot be used to purchase gift card products
    if (this.hasGiftCardItems()) {
      throw new Error(
        "Gift cards cannot be used to purchase gift card products"
      )
    }

    // Coupons and gift cards are mutually exclusive
    if (this.discount) {
      this.removeCoupon({ noTrack: true })
    }

    this.beamCartEvent({
      action: "addGiftCard",
      contents: { code },
      operation: "update",
    })

    // Validate gift card with backend
    const response = await fetch("/api/cart/giftcard", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code,
        region: this.region,
        currency: this.currency,
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      this.beamCartEvent({
        action: "addGiftCardFail",
        contents: { code },
        operation: "update",
      })
      throw new Error(error.message || "Invalid gift card")
    }

    const giftCardData = await response.json()

    if (!giftCardData.balance || giftCardData.balance <= 0) {
      this.beamCartEvent({
        action: "addGiftCardFail",
        contents: { code },
        operation: "update",
      })
      throw new Error("Invalid gift card")
    }

    // Validate gift card currency matches cart currency
    if (!giftCardData.currency || giftCardData.currency !== this.currency) {
      this.beamCartEvent({
        action: "addGiftCardFail",
        contents: { code },
        operation: "update",
      })
      throw new Error(
        giftCardData.currency
          ? `This gift card (${giftCardData.currency}) cannot be used with your current currency (${this.currency})`
          : "This gift card has an unknown currency and cannot be applied"
      )
    }

    const amountToApply = Math.min(giftCardData.balance, this.totals.grandTotal)

    this.giftCard = {
      code,
      amount: amountToApply,
      currency: giftCardData.currency,
    }

    this.recalculateTotals()
    this.saveToStorage()

    // Tracking
    trackAction("add_giftcard", {
      amount: clearDecimals(amountToApply / 100, 2),
      cart_id: this.uid,
    })

    this.emitUpdate()
  }

  /**
   * Removes the currently applied gift card from the cart
   * @param options - Optional tracking configuration
   */
  public removeGiftCard(options: TrackingOptions = {}): void {
    const { noTrack = false } = options
    const currentGiftCard = this.giftCard

    this.giftCard = undefined
    this.recalculateTotals()
    this.saveToStorage()

    // Tracking
    if (!noTrack) {
      this.beamCartEvent({
        action: "removeGiftCard",
        contents: { giftCard: currentGiftCard },
        operation: "update",
      })

      trackAction("remove_giftcard", {
        cart_id: this.uid,
      })
    }

    this.emitUpdate()
  }

  /**
   * Sets the tip amount for the cart
   * @param amount - The tip amount (will be clamped to minimum 0)
   */
  public setTip(amount: number): void {
    this.tip = Math.max(0, amount)
    this.recalculateTotals()
    this.saveToStorage()

    // Tracking
    this.beamCartEvent({
      action: "addTip",
      contents: { amount },
      operation: "update",
    })

    this.emitUpdate()
  }

  /**
   * Sets the progressive discount percentage for volume-based discounts.
   * Uses the native Cart.Discount mechanism with source "progressive".
   * Mutually exclusive with coupons — coupon takes precedence.
   * @param percentage - The discount percentage to apply (e.g., 0, 5, 10)
   */
  public setProgressiveDiscount(percentage: number): void {
    this.progressiveDiscountPercentage = Math.max(0, percentage)
    this.applyProgressiveIfEligible()
    this.recalculateTotals()
    this.saveToStorage()

    this.beamCartEvent({
      action: "setProgressiveDiscount",
      contents: { percentage },
      operation: "update",
    })

    this.emitUpdate()
  }

  /**
   * Applies the progressive discount as the cart discount if no coupon is active.
   */
  private applyProgressiveIfEligible(): void {
    // Don't override an active coupon
    if (this.discount && this.discount.source !== "progressive") return

    if (this.progressiveDiscountPercentage > 0) {
      this.discount = {
        code: "",
        amount: this.progressiveDiscountPercentage,
        type: "percentage",
        minSpend: 0,
        source: "progressive",
      }
    } else {
      // Clear progressive discount if percentage is 0
      if (this.discount?.source === "progressive") {
        this.discount = undefined
      }
    }
  }

  /**
   * Sets the tax information for the cart
   * @param taxInfo - The tax information including amount and status
   */
  public setTax(taxInfo: Cart.TaxInfo, skipEvent?: boolean): void {
    this.tax = taxInfo
    this.recalculateTotals()
    this.saveToStorage()

    // Tracking
    this.beamCartEvent({
      action: "setTax",
      contents: { taxInfo },
      operation: "update",
    })

    if (!skipEvent) {
      this.emitUpdate()
    }
  }

  /**
   * Sets the shipping method for the cart (Standard or Express)
   * Express shipping is only available for US orders
   * @param shippingMethod - The shipping method to set
   */
  public setShippingMethod(shippingMethod: ShippingMethod): void {
    // Express shipping is only available for US
    if (
      shippingMethod === ShippingMethod.SUPER_EXPRESS &&
      this.region !== "us"
    ) {
      return
    }

    // Express shipping is not available for carts with pre-order items
    if (
      shippingMethod === ShippingMethod.SUPER_EXPRESS &&
      hasPreOrderItems(this.items)
    ) {
      return
    }

    this.shippingMethod = shippingMethod
    this.recalculateTotals()
    this.saveToStorage()

    // Tracking
    this.beamCartEvent({
      action: "setShippingMethod",
      contents: { shippingMethod },
      operation: "update",
    })

    this.emitUpdate()
  }

  /**
   * Gets the current shipping method
   * @returns The current shipping method
   */
  public getShippingMethod(): ShippingMethod {
    return this.shippingMethod
  }

  /**
   * Sets a custom shipping cost (e.g., from express shipping API calculation)
   * This overrides the calculated shipping cost in recalculateTotals
   * @param amount - The shipping cost in cents, or undefined to clear
   */
  public setShippingCost(amount: number | undefined): void {
    this.customShippingCost = amount
    this.recalculateTotals()
    this.saveToStorage()

    // Tracking
    this.beamCartEvent({
      action: "setShippingCost",
      contents: { amount },
      operation: "update",
    })

    this.emitUpdate()
  }

  /**
   * Gets the current custom shipping cost
   * @returns The custom shipping cost if set, undefined otherwise
   */
  public getShippingCost(): number | undefined {
    return this.customShippingCost
  }

  /**
   * Checks if express shipping is available for the current region
   * Express shipping is only available in the US market
   * Actual availability depends on zip code (checked via shipping API)
   * @returns True if express shipping may be available
   */
  public isExpressShippingAvailable(): boolean {
    // TODO: Re-enable when express shipping is active.
    // Must check: this.region === "us" && !hasPreOrderItems(this.items)
    return false
  }

  /**
   * Clears all items from the cart and resets to empty state
   * @param options - Optional tracking configuration
   */
  public clearCart(options: TrackingOptions = {}): void {
    const { noTrack = false } = options

    this.items = []
    this.discount = undefined
    this.giftCard = undefined
    this.shippingMethod = ShippingMethod.STANDARD
    this.tip = 0
    this.progressiveDiscountPercentage = 0
    this.convertedCart = null
    this.totals = this.getEmptyTotals()
    this.tax = { amount: 0, status: "estimated" }
    this.lastUpdated = new Date().toISOString()

    // Tracking fires with the original cart IDs before regeneration
    if (!noTrack) {
      this.beamCartEvent({
        action: "resetCart",
        contents: {},
        operation: "delete",
      })
    }

    this.id = generateUniqueId()
    this.uid = generateUserFacingId()

    this.saveToStorage()

    this.emitUpdate()
  }

  /**
   * Marks cart as converted to order without clearing.
   * Erases cleanup cooldown to trigger cleanup on next page.
   */
  public markAsConverted(): void {
    // Already converted, skip
    if (this.convertedCart) {
      console.error(
        new Error("Attempted to convert an already converted cart"),
        { cartId: this.id, convertedAt: this.convertedCart }
      )
      this.clearCart({ noTrack: true })
      return
    }

    // No items to convert (should not happen in normal flow)
    if (this.items.length === 0) {
      console.error(new Error("Attempted to convert a cart with no items"), {
        cartId: this.id,
      })
      return
    }

    this.convertedCart = new Date().toISOString()
    this.lastUpdated = new Date().toISOString()
    this.saveToStorage()
    this.emitUpdate()

    // Erase cleanup cooldown to trigger cleanup on next page
    eraseCleanupCooldown()
  }

  /**
   * Changes the cart region and currency, loading existing cart for new region if available
   * @param newRegion - The new region to switch to (e.g., 'us', 'eu', 'uk', 'ca')
   */
  public changeRegion(newRegion: string): void {
    // Save current cart state
    this.saveToStorage()

    // Update region and currency
    this.region = newRegion.toLowerCase()
    this.currency = this.determineCurrencyForRegion(newRegion)

    // Update storage handler
    this.storage = new CartStorage(newRegion, this.channel)

    // Try to load existing cart for new region
    const existingCart = this.storage.load()

    if (existingCart) {
      this.loadCartData(existingCart)
    } else {
      this.clearCart()
    }

    this.emitUpdate()
  }

  /**
   * Restores cart state from a server snapshot payload
   * @param snapshot - The cart snapshot from the server
   * @param options - Optional tracking configuration
   */
  public async restoreFromSnapshot(
    snapshot: Cart.StateData,
    options: TrackingOptions = {}
  ): Promise<void> {
    const { noTrack = false } = options

    try {
      this.loadCartData(snapshot)
      this.saveToStorage()

      if (!noTrack) {
        this.beamCartEvent({
          action: "restoreFromSnapshot",
          contents: {
            itemCount: this.items.length,
            grandTotal: this.totals.grandTotal,
            source: "server_snapshot",
          },
          operation: "update",
        })
      }

      this.emitUpdate()
    } catch (error) {
      console.error("Failed to restore cart from snapshot:", error)
      throw error
    }
  }

  /**
   * Subscribes to cart updates
   * @param listener - Function to call when cart state changes
   * @returns Unsubscribe function to remove the listener
   */
  public subscribe(listener: (_cart: CartManager) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  // Getters for state access

  /**
   * Gets the complete cart state data
   * @returns A copy of the current cart state including items, totals, and metadata
   */
  public getState(): Cart.StateData {
    const baseState = {
      channel: this.channel,
      convertedCart: this.convertedCart || null,
      items: [...this.items],
      region: this.region,
      currency: this.currency,
      customShippingCost: this.customShippingCost,
      id: this.id,
      uid: this.uid,
      progressiveDiscountPercentage: this.progressiveDiscountPercentage,
      shippingMethod: this.shippingMethod,
      totals: { ...this.totals },
      tip: this.tip,
      tax: { ...this.tax },
      lastUpdated: this.lastUpdated,
    }

    // Return appropriate union type based on what's present
    if (this.discount) {
      return {
        ...baseState,
        discount: { ...this.discount },
        giftCard: undefined,
      }
    }

    if (this.giftCard) {
      return {
        ...baseState,
        discount: undefined,
        giftCard: { ...this.giftCard },
      }
    }

    return {
      ...baseState,
      discount: undefined,
      giftCard: undefined,
    }
  }

  /**
   * Gets all items in the cart
   * @returns A copy of the cart items array
   */
  public getItems(): Cart.Item[] {
    return [...this.items]
  }

  /**
   * Retrieves a specific cart item by its ID
   * @param id - The unique identifier of the item
   * @returns The cart item if found, undefined otherwise
   */
  public getItemById(id: string): Cart.Item | undefined {
    return this.items.find((item) => item.id === id)
  }

  /**
   * Gets the cart totals including subtotal, tax, shipping, discounts, etc.
   * @returns A copy of the current cart totals
   */
  public getTotals(): Cart.Totals {
    return { ...this.totals }
  }

  /**
   * Gets the total number of items in the cart
   * @returns The count of items in the cart
   */
  public getItemCount(): number {
    return this.items.length
  }

  /**
   * Checks if the cart is empty
   * @returns True if the cart has no items, false otherwise
   */
  public isEmpty(): boolean {
    return this.items.length === 0
  }

  // Private methods

  /**
   * Recalculates all cart totals including discounts, tax, shipping, and gift cards
   */
  private recalculateTotals(): void {
    // Check if cart contains gift card items (e.g., SWATGIFT)
    const hasGiftCardItems = this.hasGiftCardItems()

    // Calculate subtotal with item-level discounts
    const subtotal = this.items.reduce((total, item) => {
      let itemTotal = item.discountPrice

      // Apply item-level discount if present (but not to gift card items)
      if (item.discount && !isGiftCardItem(item)) {
        if (item.discount.type === "percentage") {
          itemTotal -= itemTotal * (item.discount.amount / 100)
        } else {
          itemTotal -= item.discount.amount
        }
      }

      return total + Math.max(0, itemTotal)
    }, 0)

    // Calculate subtotal of discountable items (excludes gift card items)
    const discountableSubtotal = this.items
      .filter((item) => !isGiftCardItem(item))
      .reduce((total, item) => {
        let itemTotal = item.discountPrice

        if (item.discount) {
          if (item.discount.type === "percentage") {
            itemTotal -= itemTotal * (item.discount.amount / 100)
          } else {
            itemTotal -= item.discount.amount
          }
        }

        return total + Math.max(0, itemTotal)
      }, 0)

    // Calculate cart-level discount (only applies to non-gift card items)
    let cartDiscount = 0

    if (
      this.discount &&
      !hasGiftCardItems &&
      discountableSubtotal >= this.discount.minSpend
    ) {
      if (this.discount.type === "percentage") {
        cartDiscount = discountableSubtotal * (this.discount.amount / 100)
      } else {
        cartDiscount = Math.min(this.discount.amount, discountableSubtotal)
      }
    }

    // Use custom shipping cost if set (e.g., from express shipping API),
    // otherwise calculate based on shipping config
    const shipping =
      this.customShippingCost !== undefined
        ? this.customShippingCost
        : this.calculateShipping(subtotal)

    // Calculate tax - use manually set tax if available, otherwise calculate automatically
    let tax: number = 0
    if (this.tax.status === "final" || this.tax.amount > 0) {
      // Use manually set tax amount
      tax = this.tax.amount
    }

    // Apply gift card payment method (cannot be used if cart contains gift card items)
    const beforeGiftCard = subtotal - cartDiscount + tax + shipping + this.tip
    const giftCardAmount =
      this.giftCard && !hasGiftCardItems
        ? Math.min(this.giftCard.amount, beforeGiftCard)
        : 0

    // Calculate grand total
    const grandTotal = beforeGiftCard - giftCardAmount

    this.totals = {
      subtotal: clearDecimals(subtotal, 0),
      tax: clearDecimals(tax, 0),
      shipping: clearDecimals(shipping, 0),
      discount: clearDecimals(cartDiscount, 0),
      giftCard: clearDecimals(giftCardAmount, 0),
      tip: clearDecimals(this.tip, 0),
      grandTotal: clearDecimals(Math.max(0, grandTotal), 0),
    }

    this.lastUpdated = new Date().toISOString()
  }

  /**
   * Reindexes all cart items to ensure correct sequential indices
   */
  private reindexItems(): void {
    this.items = this.items
      .sort((a, b) => a.index - b.index)
      .map((item, index) => ({
        ...item,
        index,
      }))
  }

  /**
   * Finds the best matching shipping configuration using fallback logic
   * Priority: channel+region > channel+* > *+region > *+*
   * @param channel - The channel identifier
   * @param region - The region identifier
   * @param configs - Array of shipping configurations
   * @returns The best matching configuration
   */
  private findShippingConfig(
    channel: string,
    region: string,
    configs: ShippingCostConfigType[] = SHIPPING_COSTS_CONFIG
  ): ShippingCostConfigType {
    // Create a map for O(1) lookups
    const configMap = new Map<string, ShippingCostConfigType>()

    for (const config of configs) {
      const key = `${config.channel}:${config.region}`
      configMap.set(key, config)
    }

    // Priority order: exact match > channel+wildcard > wildcard+region > wildcard+wildcard
    const lookupKeys = [
      `${channel}:${region}`, // Exact match
      `${channel}:*`, // Channel match with wildcard region
      `*:${region}`, // Wildcard channel with region match
      `*:*`, // Full wildcard fallback
    ]

    for (const key of lookupKeys) {
      const config = configMap.get(key)
      if (config) {
        return config
      }
    }

    // Default to global fallback
    console.error("No shipping config found, returning global fallback")
    return SHIPPING_GLOBAL_FALLBACK_CONFIG
  }

  /**
   * Calculates shipping cost based on cart contents and region
   * @param currentSubtotal The current subtotal to use for free shipping calculation
   * @returns The shipping cost amount
   */
  private calculateShipping(currentSubtotal: number): number {
    if (this.items.length === 0) return 0
    if (this.channel === "") return 0

    // Gift cards are digital products - no shipping required
    if (areAllItemsGiftCards(this.items)) return 0

    // Find the best matching shipping configuration
    const shippingConfig = this.findShippingConfig(this.channel, this.region)

    // Note: Express shipping cost is now calculated dynamically based on zip code
    // via the shipping API. The cart state uses standard shipping for totals display.
    // The actual express shipping cost is calculated at checkout time.

    const baseShipping = shippingConfig?.shippingCost ?? 0
    const freeShippingThreshold = shippingConfig?.freeShippingThreshold ?? 0

    // Apply free shipping if threshold is met
    return currentSubtotal >= freeShippingThreshold ? 0 : baseShipping
  }

  /**
   * Determines the currency code for a given region
   * @param region - The region identifier
   * @returns The currency code (e.g., 'USD', 'EUR', 'GBP')
   */
  private determineCurrencyForRegion(region: string): string {
    const currencyMap: Record<string, string> = {
      au: "AUD",
      ca: "CAD",
      ch: "CHF",
      eu: "EUR",
      gb: "GBP",
      nz: "NZD",
      us: "USD",
    }

    return currencyMap[region] || "USD"
  }

  /**
   * Creates an empty totals object with all values set to 0
   * @returns Empty cart totals
   */
  private getEmptyTotals(): Cart.Totals {
    return {
      subtotal: 0,
      tax: 0,
      shipping: 0,
      discount: 0,
      giftCard: 0,
      tip: 0,
      grandTotal: 0,
    }
  }

  /**
   * Loads cart data from localStorage if available
   */
  private loadFromStorage(): void {
    const stored = this.storage.load()
    if (stored) {
      this.loadCartData(stored)
    }
  }

  /**
   * Loads cart data from a StateData object
   * @param data - The cart state data to load
   */
  private loadCartData(data: Cart.StateData): void {
    if (data.id) this.id = data.id
    if (data.uid) this.uid = data.uid
    this.items = (data.items || []).map(withFinalDiscountPrice)
    this.discount = data.discount
    this.giftCard = data.giftCard

    // Invalidate gift card if currency doesn't match current cart currency
    if (
      this.giftCard &&
      this.giftCard.currency &&
      this.giftCard.currency !== this.currency
    ) {
      this.giftCard = undefined
    }
    this.shippingMethod = data.shippingMethod || ShippingMethod.STANDARD
    this.customShippingCost = data.customShippingCost

    // Validate: express shipping is not allowed with pre-order items
    if (
      this.shippingMethod === ShippingMethod.SUPER_EXPRESS &&
      hasPreOrderItems(this.items)
    ) {
      this.shippingMethod = ShippingMethod.STANDARD
      this.customShippingCost = undefined
    }
    this.convertedCart = data.convertedCart || null
    this.tip = data.tip || 0
    this.progressiveDiscountPercentage = data.progressiveDiscountPercentage || 0
    this.applyProgressiveIfEligible()
    this.tax = data.tax || { amount: 0, status: "estimated" }
    this.lastUpdated = data.lastUpdated || new Date().toISOString()

    // Recalculate totals to ensure consistency
    this.recalculateTotals()
  }

  /**
   * Saves the current cart state to localStorage
   */
  private saveToStorage(): void {
    this.storage.save(this.getState())
  }

  /**
   * Sets up cross-tab synchronization by listening to localStorage events
   */
  private setupSynchronization(): void {
    // Avoid SSR issues
    if (typeof window === "undefined") {
      return
    }

    // Listen for storage changes from other tabs
    window?.addEventListener?.("storage", (event) => {
      if (event.key === this.storage.getStorageKey()) {
        const updatedData = this.storage.load()
        if (updatedData) {
          this.loadCartData(updatedData)
          this.emitUpdate()
        }
      }
    })
  }

  /**
   * Emits cart update events to all listeners and dispatches custom events
   */
  private emitUpdate(): void {
    // Notify all listeners
    this.listeners.forEach((listener) => listener(this))

    // Emit custom event for local updates
    window.dispatchEvent(
      new CustomEvent("cart-updated", {
        detail: {
          channel: this.channel,
          region: this.region,
          state: this.getState(),
        },
      })
    )
  }

  /**
   * Sends cart events to the tracking backend
   * @param input - The event data to send
   */
  public async beamCartEvent(input: {
    action: string
    contents: object
    operation: "create" | "update" | "delete" | "track"
  }): Promise<void> {
    const endpoint = `${APP_URL}/api/cart/event/`

    if (this.id && this.uid) {
      const data: CartEventInput = {
        action: input.action,
        channel:
          typeof window !== "undefined" && window.channel
            ? window.channel
            : "store",
        cookies: typeof document !== "undefined" ? document.cookie : "",
        contents: input.contents,
        currency: this.currency,
        id: this.id,
        operation: input.operation,
        snapshot: this.getState(),
        uid: this.uid,
      }

      const blob = new Blob([objectToShiftedBase64(data, 24)], {
        type: "text/plain",
      })
      sendBeacon(endpoint, blob)
    }
  }

  /**
   * Tracks add to cart events
   * @param items - Items being added to the cart
   * @param trackActionBuffered - Optional custom tracking function
   * @param location - Optional location identifier
   */
  private trackAddToCart(
    items: Cart.Item[],
    trackActionBuffered: typeof trackAction = trackAction,
    location?: string
  ): void {
    const bundleVariantIds = items.map((item) => {
      if (item.type === "bundle") {
        return (item as Cart.BundleItem).bundleVariantId ?? null
      }
      return null
    })

    const item = items[0]
    // Parse taxonomyName into separate category levels
    const taxonomyCategories =
      item.taxonomyName?.split(">").map((cat) => cat.trim()) ?? []
    const categoryLevel1 = taxonomyCategories[0] ?? null
    const categoryLevel2 = taxonomyCategories[1] ?? null
    const categoryLevel3 = taxonomyCategories[2] ?? null

    const eventData = {
      currency: this.currency || "USD",
      value: clearDecimals(this.totals.grandTotal / 100, 2),
      value_usd: convertToUsd(this.totals.grandTotal, this.currency || "USD"),
      product_ids_list: items.map((item) => item.legacyId ?? item.listingId),
      item_product_id: item.legacyId ?? item.listingId,
      item_variant_id:
        item.type === "single" ? item.legacyVariantId : undefined,
      ...(item?.legacyId &&
        item.type === "single" && {
          internal_item_variant_id: item.legacyVariantId,
        }),
      category_level_1: categoryLevel1,
      category_level_2: categoryLevel2,
      category_level_3: categoryLevel3,
      size: item.attributes?.["size"] || undefined,
      color: item.attributes?.["color"] || undefined,
      item_price: clearDecimals(item.discountPrice / 100, 2),
      item_quantity: 1, // CartTypes uses individual items, not quantities
      total_price: clearDecimals(item.discountPrice / 100, 2), // price * quantity, quantity is always 1 for cartV6

      items: items.map((item, i) => {
        const variantName = item.attributes
          ? Object.entries(item.attributes)
              .map(([_, value]) => value)
              .join(" ")
          : undefined

        // Parse taxonomyName into separate category levels
        const taxonomyCategories =
          item.taxonomyName?.split(">").map((cat) => cat.trim()) ?? []
        const categoryLevel1 = taxonomyCategories[0] ?? null
        const categoryLevel2 = taxonomyCategories[1] ?? null
        const categoryLevel3 = taxonomyCategories[2] ?? null

        const bundleVariantId = bundleVariantIds[i]
        const itemId = bundleVariantId || item?.legacyId || item.listingId

        return {
          discount: clearDecimals((item.price - item.discountPrice) / 100, 2),
          image_url: item.media?.url,
          item_category_id: item.taxonomyId,
          category_level_1: categoryLevel1,
          category_level_2: categoryLevel2,
          category_level_3: categoryLevel3,
          item_id: itemId,
          ...(item.type === "bundle" && { item_group_id: item.listingId }),
          ...(item.type === "single" &&
            item?.legacyId && { internal_item_id: item.listingId }),
          item_name: item.name,
          item_product_id: item.listingId,
          size: item.attributes?.["size"] || undefined,
          color: item.attributes?.["color"] || undefined,
          total_price: clearDecimals(item.discountPrice / 100, 2), // price * quantity, quantity is always 1 for cartV6
          item_variant_id:
            item.type === "single" ? item.legacyVariantId : undefined,
          ...(item?.legacyId &&
            item.type === "single" && {
              internal_item_variant_id: item.legacyVariantId,
            }),
          item_variant: variantName,
          location_id: location,
          position: i + 1,
          index: i + 1,
          price: clearDecimals(item.discountPrice / 100, 2),
          quantity: 1, // CartTypes uses individual items, not quantities
        }
      }),
    }

    // Push to dataLayer for Luigi's Box analytics
    dispatchDataLayerPush({ event: "add_to_cart", ecommerce: eventData })

    trackActionBuffered("add_to_cart", eventData)
  }

  /**
   * Tracks remove from cart events
   * @param item - Item being removed from the cart
   */
  private trackRemoveFromCart(item: Cart.Item): void {
    const variantName = item.attributes
      ? Object.entries(item.attributes)
          .map(([_, value]) => value)
          .join(" ")
      : undefined

    let itemId = item?.legacyId ?? item.listingId
    if (item.type === "bundle") {
      const bundleItem = item as Cart.BundleItem
      itemId = bundleItem.bundleVariantId ?? item.listingId
    }

    const removeEventData = {
      currency: this.currency,
      value: clearDecimals(item.discountPrice / 100, 2),
      value_usd: convertToUsd(item.discountPrice, this.currency || "USD"),
      item_product_id: item.legacyId ?? item.listingId,
      item_variant_id:
        item.type === "single"
          ? (item?.legacyVariantId ?? item.sku)
          : undefined,
      items: [
        {
          item_id: itemId,
          ...(item.type === "bundle" && { item_group_id: item.listingId }),
          item_name: item.name,
          quantity: 1, // CartTypes uses individual items, not quantities
          variant: item.type === "single" ? variantName : undefined,
          item_variant_id:
            item.type === "single"
              ? (item?.legacyVariantId ?? item.sku)
              : undefined,
          image_url: item.media?.url,
          item_category_id: item.taxonomyId,
          price: clearDecimals(item.discountPrice / 100, 2),
          discount: clearDecimals((item.price - item.discountPrice) / 100, 2),
        },
      ],
    }

    trackAction("remove_from_cart", removeEventData)
  }

  /**
   * Forces a cart update event to be emitted
   * Useful if external code modifies the cart state directly
   */
  public refresh(): void {
    this.emitUpdate()
  }
}
